import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import TournamentSelector from '../components/TournamentSelector';
import LegSplitInput, { LegBadges } from '../components/LegSplit';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Package, Plus, Edit, Trash2, Calculator, IndianRupee, FileText, TrendingUp, Search, Calendar, Sparkles, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Calculate fiscal year from date
const getFiscalYearFromDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const month = date.getMonth(); // 0-11
  const year = date.getFullYear();
  
  // Fiscal year starts in April (month 3)
  if (month >= 3) {
    // April to December - current year to next year
    return `${year}-${year + 1}`;
  } else {
    // January to March - previous year to current year
    return `${year - 1}-${year}`;
  }
};

const PurchaseOrders = () => {
  const { user, hasTabAccess } = useAuth();
  const navigate = useNavigate();
  const [pos, setPos] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [budgetHeadsByDept, setBudgetHeadsByDept] = useState([]);
  const [selectedFiscalYear, setSelectedFiscalYear] = useState('');
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPO, setEditingPO] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [smartText, setSmartText] = useState('');
  // AI extract from PI (proforma invoice) — mirrors invoices flow
  const [selectedPiFile, setSelectedPiFile] = useState(null);
  const [extractedPiFile, setExtractedPiFile] = useState(null); // original File kept around for "create vendor" reuse
  const [extracting, setExtracting] = useState(false);
  const [extractMeta, setExtractMeta] = useState(null); // { matched_vendor_id, match_confidence, model, vendor_name_seen, vendor_gstin_seen }
  const [pendingDocument, setPendingDocument] = useState(null); // { filename, content_type, data_base64 }
  // "Create vendor from this PI" inline flow
  const [vendorCreateOpen, setVendorCreateOpen] = useState(false);
  const [vendorPrefilling, setVendorPrefilling] = useState(false);
  const [vendorCreating, setVendorCreating] = useState(false);
  const [vendorCreateForm, setVendorCreateForm] = useState({
    name: '', pan: '', gstin: '', bank_account_number: '', ifsc_code: '',
    email: '', phone: '', address: '', remarks: '',
  });
  
  const [formData, setFormData] = useState({
    po_number: '',
    vendor_id: '',
    date: new Date().toISOString().split('T')[0],
    department: '',
    budget_head: '',
    taxable_value: '',
    cgst: '',
    sgst: '',
    igst: '',
    tds: '',
    fiscal_year: '',
    notes: '',
    status: 'draft',
    leg_split: null,
  });
  const [legSuggestion, setLegSuggestion] = useState(null);
  // Tracks whether the user has manually touched the leg allocator. While
  // false, we auto-apply the budget-head-derived split whenever budget_head
  // or taxable_value changes. Once the user edits any leg input manually,
  // we leave their values alone.
  const [legSplitUserEdited, setLegSplitUserEdited] = useState(false);

  // Compute a leg-split suggestion AND auto-apply it to formData.leg_split
  // whenever the user picks a budget_head + has a taxable_value — as long as
  // they haven't manually edited the allocator yet. Splits using the budget
  // head's per-leg ratios (alloc_mens / alloc_womens / alloc_ground2). Falls
  // back to {common: taxable_value} when the head has no leg ratios.
  useEffect(() => {
    const tv = parseFloat(formData.taxable_value) || 0;
    if (!formData.budget_head || !formData.fiscal_year || tv <= 0) {
      setLegSuggestion(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const heads = budgetHeadsByDept.find((h) => h.head === formData.budget_head);
        let sug;
        if (!heads) {
          sug = { mens: 0, womens: 0, ground2: 0, common: tv };
        } else {
          const m = Number(heads.alloc_mens || 0);
          const w = Number(heads.alloc_womens || 0);
          const g = Number(heads.alloc_ground2 || 0);
          const total = m + w + g;
          if (total <= 0) {
            sug = { mens: 0, womens: 0, ground2: 0, common: tv };
          } else {
            sug = {
              mens: Number(((m / total) * tv).toFixed(2)),
              womens: Number(((w / total) * tv).toFixed(2)),
              ground2: Number(((g / total) * tv).toFixed(2)),
              common: 0,
            };
            // Fix rounding drift onto the largest leg so the sum matches tv exactly
            const drift = Number((tv - (sug.mens + sug.womens + sug.ground2)).toFixed(2));
            if (Math.abs(drift) >= 0.01) {
              const biggest = ['mens', 'womens', 'ground2'].reduce(
                (a, b) => (sug[a] >= sug[b] ? a : b)
              );
              sug[biggest] = Number((sug[biggest] + drift).toFixed(2));
            }
          }
        }
        if (cancelled) return;
        setLegSuggestion(sug);
        // Auto-apply if user hasn't manually overridden yet.
        if (!legSplitUserEdited) {
          setFormData((prev) => ({ ...prev, leg_split: sug }));
        }
      } catch {
        if (!cancelled) setLegSuggestion(null);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.budget_head, formData.taxable_value, formData.fiscal_year, budgetHeadsByDept]);

  // Auto-suggest a PO number whenever tournament + date are set (and the user
  // hasn't manually typed one yet, and we're not editing an existing PO).
  // Format: `{SLUG}/{FY}/PO/{NNN}` (e.g. "MPL/26-27/PO/017").
  const [poNumberAutoSuggested, setPoNumberAutoSuggested] = useState(false);
  useEffect(() => {
    if (editingPO) return; // never override an existing PO number
    if (!formData.fiscal_year || !formData.date) return;
    if (formData.po_number && !poNumberAutoSuggested) return; // user typed something
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/purchase-orders/suggest-po-number`, {
          withCredentials: true,
          params: { tournament: formData.fiscal_year, date: formData.date },
        });
        if (cancelled) return;
        setFormData((prev) => ({ ...prev, po_number: res.data.po_number }));
        setPoNumberAutoSuggested(true);
      } catch (e) {
        // Silent: user can still type manually. Log so prod issues are debuggable.
        if (e?.response?.status !== 401 && e?.response?.status !== 403) {
          console.warn('PO number auto-suggest failed:', e?.response?.data || e?.message);
        }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.fiscal_year, formData.date, editingPO]);

  // Reset the "user has manually edited the leg split" flag whenever the
  // user deliberately picks a different budget head. This way the new head's
  // ratios are auto-applied. Also reset on dialog open/close via openNewDialog.
  useEffect(() => {
    setLegSplitUserEdited(false);
  }, [formData.budget_head]);

  // Permission check
  const canManagePOs = hasTabAccess('purchase_orders');

  // Parse smart text input to extract PO details
  const parseSmartText = (text) => {
    const updates = {};
    
    // Extract PO number (patterns like PO-123, PO/123, PO123)
    const poMatch = text.match(/\b(PO[-/]?\d+)\b/i);
    if (poMatch) updates.po_number = poMatch[1].toUpperCase();
    
    // Extract amount (patterns like Rs.50000, ₹50,000, 50000/-)
    const amountMatch = text.match(/(?:Rs\.?|₹|INR)\s*([\d,]+(?:\.\d{2})?)|(\d{1,3}(?:,\d{3})*(?:\.\d{2})?)\s*\/-/i);
    if (amountMatch) {
      const amount = (amountMatch[1] || amountMatch[2]).replace(/,/g, '');
      updates.taxable_value = amount;
    }
    
    // Extract date (patterns like 15/01/2024, 15-01-2024, Jan 15 2024)
    const dateMatch = text.match(/(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})/);
    if (dateMatch) {
      const [_, day, month, year] = dateMatch;
      updates.date = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    
    // Try to match vendor name from existing vendors
    const lowerText = text.toLowerCase();
    for (const vendor of vendors) {
      if (lowerText.includes(vendor.name.toLowerCase())) {
        updates.vendor_id = vendor.id;
        break;
      }
    }
    
    // Try to match department
    for (const dept of departments) {
      if (lowerText.includes(dept.toLowerCase())) {
        updates.department = dept;
        break;
      }
    }
    
    return updates;
  };

  const handleSmartTextSubmit = (e) => {
    if (e.key === 'Enter' && smartText.trim()) {
      const parsed = parseSmartText(smartText);
      setFormData(prev => ({
        ...prev,
        ...parsed,
        // Tournament/FY is now independent of date — never auto-derive it
        // from the parsed date.
      }));
      setSmartText('');
      setDialogOpen(true);
      toast.success('Details extracted from text. Please review and complete the form.');
    }
  };

  // ---- AI extract from PI (Proforma Invoice) ----
  const handlePiFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      setSelectedPiFile(null);
      return;
    }
    const isOk =
      file.type === 'application/pdf' ||
      file.type.startsWith('image/') ||
      /\.(pdf|jpe?g|png|webp)$/i.test(file.name || '');
    if (!isOk) {
      toast.error('Please select a PDF or image (jpg/png)');
      setSelectedPiFile(null);
      return;
    }
    setSelectedPiFile(file);
  };

  const handlePiExtract = async () => {
    if (!selectedPiFile) {
      toast.error('Please select a PI file first');
      return;
    }
    setExtracting(true);
    try {
      const fd = new FormData();
      fd.append('file', selectedPiFile);
      const response = await axios.post(`${API}/purchase-orders/extract-from-pi`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (response.data.success) {
        const extracted = response.data.data || {};
        const doc = response.data.document || null;
        const isoDate = extracted.date || null;
        setFormData((prev) => ({
          ...prev,
          // Only suggest po_number if the field is empty (user still owns the value)
          po_number: prev.po_number || extracted.po_number_hint || '',
          vendor_id: extracted.matched_vendor_id || prev.vendor_id,
          date: isoDate || prev.date,
          // Tournament/FY is selected manually; never derive it from PI date.
          fiscal_year: prev.fiscal_year,
          taxable_value:
            extracted.taxable_value != null ? String(extracted.taxable_value) : prev.taxable_value,
          cgst: extracted.cgst != null ? String(extracted.cgst) : prev.cgst,
          sgst: extracted.sgst != null ? String(extracted.sgst) : prev.sgst,
          igst: extracted.igst != null ? String(extracted.igst) : prev.igst,
          tds: extracted.tds != null ? String(extracted.tds) : prev.tds,
          notes: extracted.notes || prev.notes,
        }));
        setExtractMeta({
          matched_vendor_id: extracted.matched_vendor_id || null,
          match_confidence: extracted.match_confidence || null,
          model: response.data.model || null,
          vendor_name_seen: extracted.vendor_name || null,
          vendor_gstin_seen: extracted.vendor_gstin || null,
        });
        setPendingDocument(doc);
        // Keep the original File so we can reuse it for "Create vendor from this PI"
        setExtractedPiFile(selectedPiFile);
        toast.success(
          extracted.matched_vendor_id
            ? 'PI extracted. Vendor matched — please pick department + budget head and review.'
            : 'PI extracted. Please pick the vendor, department + budget head, then review.'
        );
      } else {
        toast.error(response.data.message || 'Could not extract data from the PI');
      }
    } catch (error) {
      console.error('PI extraction error:', error);
      toast.error(error.response?.data?.detail || 'Failed to extract data from the PI');
    } finally {
      setExtracting(false);
      setSelectedPiFile(null);
    }
  };

  // ---- Create vendor from this PI (when no match in catalog) ----
  const openCreateVendorFromPi = async () => {
    if (!extractedPiFile && !pendingDocument) {
      toast.error('PI file is no longer available. Please re-upload.');
      return;
    }
    // Seed with what the PO extractor already saw, then upgrade via the
    // richer vendor extractor (PAN, bank, IFSC, email, phone, address).
    setVendorCreateForm({
      name: extractMeta?.vendor_name_seen || '',
      pan: '',
      gstin: extractMeta?.vendor_gstin_seen || '',
      bank_account_number: '',
      ifsc_code: '',
      email: '',
      phone: '',
      address: '',
      remarks: 'Created from PI during PO entry',
    });
    setVendorCreateOpen(true);
    setVendorPrefilling(true);

    try {
      // Reuse the same file (or rehydrate it from pendingDocument if state was reset)
      let fileToSend = extractedPiFile;
      if (!fileToSend && pendingDocument?.data_base64) {
        const blob = await (await fetch(
          `data:${pendingDocument.content_type};base64,${pendingDocument.data_base64}`
        )).blob();
        fileToSend = new File([blob], pendingDocument.filename || 'pi.pdf', {
          type: pendingDocument.content_type || 'application/pdf',
        });
      }
      if (!fileToSend) return;
      const fd = new FormData();
      fd.append('file', fileToSend);
      const res = await axios.post(`${API}/vendors/extract-from-invoice`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      // Merge richer fields without clobbering what the PO extractor already had
      setVendorCreateForm((prev) => ({
        name: res.data.name || prev.name,
        pan: res.data.pan || prev.pan,
        gstin: res.data.gstin || prev.gstin,
        bank_account_number: res.data.bank_account_number || prev.bank_account_number,
        ifsc_code: res.data.ifsc_code || prev.ifsc_code,
        email: res.data.email || prev.email,
        phone: res.data.phone || prev.phone,
        address: res.data.address || prev.address,
        remarks: res.data.remarks || prev.remarks,
      }));
    } catch (e) {
      console.warn('Vendor pre-fill from PI failed; user can still type manually', e);
    } finally {
      setVendorPrefilling(false);
    }
  };

  const submitCreateVendorFromPi = async (e) => {
    e.preventDefault();
    if (!vendorCreateForm.name?.trim()) {
      toast.error('Vendor name is required');
      return;
    }
    setVendorCreating(true);
    try {
      const createRes = await axios.post(`${API}/vendors`, vendorCreateForm, {
        withCredentials: true,
      });
      const newVendorId = createRes.data?.vendor_id || createRes.data?.id;
      // Attach the PI as a KYC doc on the new vendor
      let fileToAttach = extractedPiFile;
      if (!fileToAttach && pendingDocument?.data_base64) {
        const blob = await (await fetch(
          `data:${pendingDocument.content_type};base64,${pendingDocument.data_base64}`
        )).blob();
        fileToAttach = new File([blob], pendingDocument.filename || 'pi.pdf', {
          type: pendingDocument.content_type || 'application/pdf',
        });
      }
      if (newVendorId && fileToAttach) {
        try {
          const fd = new FormData();
          fd.append('file', fileToAttach);
          await axios.post(
            `${API}/vendors/${newVendorId}/kyc?category=other&label=${encodeURIComponent('Source PI (AI extracted)')}`,
            fd,
            {
              withCredentials: true,
              headers: { 'Content-Type': 'multipart/form-data' },
            }
          );
        } catch (kycErr) {
          console.warn('KYC attach failed (vendor still created)', kycErr);
        }
      }
      // Refresh vendor list and auto-select the new one in the PO form
      await fetchVendors();
      if (newVendorId) {
        setFormData((prev) => ({ ...prev, vendor_id: newVendorId }));
        setExtractMeta((prev) => prev ? { ...prev, matched_vendor_id: newVendorId, match_confidence: 'high' } : prev);
      }
      toast.success('Vendor created and selected for this PO');
      setVendorCreateOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create vendor');
    } finally {
      setVendorCreating(false);
    }
  };

  const [legFilter, setLegFilter] = useState('all'); // all | mens | womens | ground2 | common | untagged

  // Filter POs based on search term and date range
  const filteredPOs = pos.filter(po => {
    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const matchesSearch = (
        po.po_number?.toLowerCase().includes(term) ||
        po.vendor_name?.toLowerCase().includes(term) ||
        po.budget_head?.toLowerCase().includes(term) ||
        po.department?.toLowerCase().includes(term) ||
        po.notes?.toLowerCase().includes(term)
      );
      if (!matchesSearch) return false;
    }
    
    // Date range filter
    if (dateFrom || dateTo) {
      const poDate = new Date(po.date);
      if (dateFrom && poDate < new Date(dateFrom)) return false;
      if (dateTo && poDate > new Date(dateTo)) return false;
    }

    // Leg filter — checks the leg_split bucket has a positive value.
    if (legFilter !== 'all') {
      if (legFilter === 'untagged') {
        if (po.leg_split && Object.values(po.leg_split).some((v) => Number(v || 0) > 0)) return false;
      } else {
        const v = Number(po.leg_split?.[legFilter] || 0);
        if (v <= 0) return false;
      }
    }
    
    return true;
  });

  useEffect(() => {
    if (!selectedFiscalYear) return;
    fetchPOs();
    fetchVendors();
  }, [selectedFiscalYear]);

  // Refetch departments whenever the selected tournament in the form changes
  useEffect(() => {
    fetchDepartments(formData.fiscal_year);
    // Clear budget head options when tournament changes (department list may differ)
  }, [formData.fiscal_year]);

  useEffect(() => {
    if (formData.department) {
      fetchBudgetHeadsByDepartment(formData.department, formData.fiscal_year);
    } else {
      setBudgetHeadsByDept([]);
    }
  }, [formData.department, formData.fiscal_year]);

  const fetchPOs = async () => {
    setLoading(true);
    try {
      const fiscalParam = selectedFiscalYear !== 'all' ? `?fiscal_year=${selectedFiscalYear}` : '';
      const response = await axios.get(`${API}/purchase-orders${fiscalParam}`, {
        withCredentials: true,
      });
      setPos(response.data);
    } catch (error) {
      toast.error('Failed to load purchase orders');
    } finally {
      setLoading(false);
    }
  };

  const fetchVendors = async () => {
    try {
      const response = await axios.get(`${API}/vendors`, { withCredentials: true });
      setVendors(response.data);
    } catch (error) {
      console.error('Failed to load vendors');
    }
  };

  const fetchDepartments = async (tournament = '') => {
    try {
      const url = tournament
        ? `${API}/metadata/departments?tournament=${encodeURIComponent(tournament)}`
        : `${API}/metadata/departments`;
      const response = await axios.get(url, { withCredentials: true });
      setDepartments(response.data);
    } catch (error) {
      console.error('Failed to load departments');
    }
  };

  const fetchBudgetHeadsByDepartment = async (department, tournament = '') => {
    try {
      const params = new URLSearchParams({ department });
      if (tournament) params.append('tournament', tournament);
      const response = await axios.get(
        `${API}/metadata/budget-heads?${params.toString()}`,
        { withCredentials: true }
      );
      // Backend returns either a list of strings (legacy, no tournament) OR a
      // list of {head, alloc_mens, alloc_womens, alloc_ground2, allocated_amount}
      // objects (when tournament is provided). Normalize to objects so the
      // rest of the form has a single shape to work with.
      const raw = Array.isArray(response.data) ? response.data : (response.data.budget_heads || []);
      const normalized = raw.map((item) =>
        typeof item === 'string'
          ? { head: item, alloc_mens: null, alloc_womens: null, alloc_ground2: null, allocated_amount: null }
          : item
      );
      setBudgetHeadsByDept(normalized);
    } catch (error) {
      console.error('Failed to load budget heads:', error);
      setBudgetHeadsByDept([]);
    }
  };

  const calculateNetPayable = () => {
    const taxable = parseFloat(formData.taxable_value) || 0;
    const cgst = parseFloat(formData.cgst) || 0;
    const sgst = parseFloat(formData.sgst) || 0;
    const igst = parseFloat(formData.igst) || 0;
    const tds = parseFloat(formData.tds) || 0;
    
    return taxable + cgst + sgst + igst - tds;
  };

  const resetForm = () => {
    setFormData({
      po_number: '',
      vendor_id: '',
      date: new Date().toISOString().split('T')[0],
      department: '',
      budget_head: '',
      taxable_value: '',
      cgst: '',
      sgst: '',
      igst: '',
      tds: '',
      fiscal_year: selectedFiscalYear && selectedFiscalYear !== 'all' ? selectedFiscalYear : '',
      notes: '',
      status: 'draft',
      leg_split: null,
    });
    setEditingPO(null);
    setBudgetHeadsByDept([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const tv = parseFloat(formData.taxable_value) || 0;
      // Default leg_split to {common: taxable_value} when the user didn't
      // touch the leg allocator. Keeps existing PO entry frictionless.
      const legSplit = formData.leg_split && Object.values(formData.leg_split).some((v) => Number(v || 0) > 0)
        ? formData.leg_split
        : { mens: 0, womens: 0, ground2: 0, common: tv };
      const payload = {
        ...formData,
        taxable_value: tv,
        cgst: parseFloat(formData.cgst) || 0,
        sgst: parseFloat(formData.sgst) || 0,
        igst: parseFloat(formData.igst) || 0,
        tds: parseFloat(formData.tds) || 0,
        date: new Date(formData.date).toISOString(),
        leg_split: legSplit,
      };

      if (editingPO) {
        await axios.put(`${API}/purchase-orders/${editingPO.id}`, payload, {
          withCredentials: true,
        });
        toast.success('Purchase order updated successfully');
      } else {
        const createRes = await axios.post(`${API}/purchase-orders/manual`, payload, {
          withCredentials: true,
        });
        toast.success('Purchase order created successfully');
        // Auto-attach the original PI document the user extracted from
        const newPoId = createRes.data?.po_id || createRes.data?.id;
        if (newPoId && pendingDocument?.data_base64) {
          try {
            const blob = await (await fetch(
              `data:${pendingDocument.content_type};base64,${pendingDocument.data_base64}`
            )).blob();
            const fd = new FormData();
            fd.append(
              'file',
              new File([blob], pendingDocument.filename || 'pi.pdf', {
                type: pendingDocument.content_type || 'application/pdf',
              })
            );
            await axios.post(`${API}/purchase-orders/${newPoId}/attachments`, fd, {
              withCredentials: true,
              headers: { 'Content-Type': 'multipart/form-data' },
            });
            toast.success('PI document attached to the PO');
          } catch (attachErr) {
            console.warn('Failed to auto-attach PI document', attachErr);
            toast.error('PO created but failed to attach the PI document');
          }
        }
      }

      setDialogOpen(false);
      resetForm();
      setExtractMeta(null);
      setPendingDocument(null);
      setExtractedPiFile(null);
      fetchPOs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save purchase order');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (po) => {
    setEditingPO(po);
    setExtractMeta(null);
    setPendingDocument(null);
    setSelectedPiFile(null);
    setExtractedPiFile(null);
    // For existing POs, the leg_split is whatever was saved — preserve it.
    // Mark as user-edited so auto-apply doesn't clobber an existing split.
    setLegSplitUserEdited(true);
    setFormData({
      po_number: po.po_number,
      vendor_id: po.vendor_id || '',
      date: new Date(po.date).toISOString().split('T')[0],
      department: po.department || '',
      budget_head: po.budget_head || '',
      taxable_value: po.taxable_value?.toString() || '',
      cgst: po.cgst?.toString() || '',
      sgst: po.sgst?.toString() || '',
      igst: po.igst?.toString() || '',
      tds: po.tds?.toString() || '',
      fiscal_year: po.fiscal_year || '',
      notes: po.notes || '',
      status: po.status || 'draft',
      leg_split: po.leg_split || null,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (poId) => {
    if (!window.confirm('Are you sure you want to delete this purchase order?')) {
      return;
    }

    try {
      await axios.delete(`${API}/purchase-orders/${poId}`, { withCredentials: true });
      toast.success('Purchase order deleted');
      fetchPOs();
    } catch (error) {
      toast.error('Failed to delete purchase order');
    }
  };

  const openNewDialog = () => {
    resetForm();
    setExtractMeta(null);
    setPendingDocument(null);
    setSelectedPiFile(null);
    setExtractedPiFile(null);
    setLegSplitUserEdited(false);
    setPoNumberAutoSuggested(false);
    setDialogOpen(true);
  };

  // Group POs by department (use filtered POs)
  const groupedPOs = filteredPOs.reduce((acc, po) => {
    const dept = po.department || 'Uncategorized';
    if (!acc[dept]) acc[dept] = [];
    acc[dept].push(po);
    return acc;
  }, {});

  // Calculate summary metrics (use all POs for totals)
  const summaryMetrics = {
    totalPOs: pos.length,
    totalTaxableValue: pos.reduce((sum, po) => sum + (po.taxable_value || 0), 0),
    totalNetPayable: pos.reduce((sum, po) => sum + (po.net_payable || 0), 0),
    totalOpenValue: pos.reduce((sum, po) => sum + (po.open_value || po.taxable_value || 0), 0),
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div data-testid="purchase-orders-page" className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-heading font-bold tracking-tight">
            Purchase Orders
          </h1>
          <p className="text-muted-foreground mt-1">Manage purchase orders</p>
        </div>
        <div className="flex gap-3 flex-wrap justify-end">
          {/* Leg filter chips */}
          <div className="flex gap-1 items-center" data-testid="po-leg-filter">
            {[
              { v: 'all', label: 'All legs' },
              { v: 'mens', label: 'Mens' },
              { v: 'womens', label: 'Womens' },
              { v: 'ground2', label: 'Ground 2' },
              { v: 'common', label: 'Common' },
              { v: 'untagged', label: 'Untagged' },
            ].map((f) => (
              <Button
                key={f.v}
                size="sm"
                variant={legFilter === f.v ? 'default' : 'outline'}
                className="h-8 text-xs"
                onClick={() => setLegFilter(f.v)}
              >
                {f.label}
              </Button>
            ))}
          </div>
          {/* Search Input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search POs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 w-48"
              data-testid="search-po-input"
            />
          </div>
          {/* Date Filters */}
          <div className="flex gap-2 items-center">
            <Input
              type="date"
              placeholder="From"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="w-36"
              data-testid="date-from-input"
            />
            <span className="text-muted-foreground">to</span>
            <Input
              type="date"
              placeholder="To"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="w-36"
              data-testid="date-to-input"
            />
          </div>
          <TournamentSelector 
            value={selectedFiscalYear}
            onChange={setSelectedFiscalYear}
            showAllOption={true}
            className="w-48"
          />
          {user?.is_super_admin && (
            <Button
              size="sm"
              variant="outline"
              onClick={async () => {
                if (!window.confirm('Tag every PO without a leg split as 100% Common? You can reassign individual POs later.')) return;
                try {
                  const fy = selectedFiscalYear && selectedFiscalYear !== 'all' ? `&fiscal_year=${encodeURIComponent(selectedFiscalYear)}` : '';
                  const res = await axios.post(
                    `${API}/purchase-orders/backfill-leg-split?mode=common&dry_run=false${fy}`,
                    {},
                    { withCredentials: true }
                  );
                  toast.success(`Backfilled ${res.data.updated} PO(s) as Common`);
                  fetchPOs();
                } catch (e) {
                  toast.error(e.response?.data?.detail || 'Backfill failed');
                }
              }}
              data-testid="po-backfill-leg-btn"
            >
              Tag untagged → Common
            </Button>
          )}
          {canManagePOs && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-po-button" data-write-only onClick={openNewDialog}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add PO
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-heading">
                    {editingPO ? 'Edit Purchase Order' : 'Create Purchase Order'}
                  </DialogTitle>
                  <DialogDescription>
                    Enter PO details with tax breakdown
                  </DialogDescription>
                </DialogHeader>
                {/* Smart Fill — Quick Text or AI PI Upload */}
                <Tabs defaultValue="text" className="w-full">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="text" className="flex items-center gap-2" data-testid="po-tab-text">
                      <Sparkles className="h-4 w-4" />
                      Quick Fill (Text)
                    </TabsTrigger>
                    <TabsTrigger value="pi" className="flex items-center gap-2" data-testid="po-tab-pi">
                      <Sparkles className="h-4 w-4" />
                      AI Extract (PI / PDF / Image)
                    </TabsTrigger>
                  </TabsList>
                  <TabsContent value="text" className="mt-2">
                    <div className="p-3 bg-muted/30 rounded-lg border border-dashed border-border">
                      <Input
                        placeholder="Paste PO text here (e.g., 'PO-123 for Vendor ABC Rs.50,000 dated 15/01/2024') and press Enter"
                        value={smartText}
                        onChange={(e) => setSmartText(e.target.value)}
                        onKeyDown={handleSmartTextSubmit}
                        className="text-sm"
                        data-testid="smart-text-input"
                      />
                    </div>
                  </TabsContent>
                  <TabsContent value="pi" className="mt-2">
                    <div className="p-3 bg-muted/30 rounded-lg border border-dashed border-border">
                      <div className="flex items-center gap-3 flex-wrap">
                        <Input
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/*"
                          onChange={handlePiFileSelect}
                          className="text-sm"
                          data-testid="po-pi-upload-input"
                          disabled={extracting}
                        />
                        <Button
                          type="button"
                          variant="default"
                          size="sm"
                          onClick={handlePiExtract}
                          disabled={!selectedPiFile || extracting}
                          data-testid="po-ai-extract-button"
                          className="bg-gradient-to-r from-[#00204F] to-[#FF7F00] text-white hover:brightness-110"
                        >
                          {extracting ? (
                            <>
                              <Sparkles className="h-4 w-4 mr-2 animate-pulse" />
                              Reading PI…
                            </>
                          ) : (
                            <>
                              <Sparkles className="h-4 w-4 mr-2" />
                              Extract with AI
                            </>
                          )}
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground mt-2">
                        Upload the vendor's Proforma Invoice (PDF or image). The AI will read it,
                        match the vendor, and pre-fill the form below. You still pick the{' '}
                        <strong>department</strong> and <strong>budget head</strong>. The original
                        PI will be attached to this PO automatically on save.
                      </p>
                      {extractMeta && (
                        <div
                          className="mt-3 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900 space-y-1"
                          data-testid="po-ai-extract-summary"
                        >
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold">AI extracted</span>
                            {extractMeta.model && (
                              <span className="px-1.5 py-0.5 rounded bg-white border text-[10px] font-mono">
                                {extractMeta.model}
                              </span>
                            )}
                            {extractMeta.match_confidence && (
                              <span
                                className={`px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider ${
                                  extractMeta.match_confidence === 'high'
                                    ? 'bg-emerald-600 text-white'
                                    : extractMeta.match_confidence === 'medium'
                                    ? 'bg-amber-500 text-white'
                                    : 'bg-rose-500 text-white'
                                }`}
                              >
                                Vendor match: {extractMeta.match_confidence}
                              </span>
                            )}
                          </div>
                          <div className="text-emerald-800/80">
                            Detected vendor:{' '}
                            <strong>{extractMeta.vendor_name_seen || '—'}</strong>
                            {extractMeta.vendor_gstin_seen
                              ? ` · GSTIN ${extractMeta.vendor_gstin_seen}`
                              : ''}
                            .{' '}
                            {extractMeta.matched_vendor_id
                              ? 'Matched to a vendor in your catalog.'
                              : 'No vendor in your catalog matched — please pick one below.'}
                          </div>
                          {!extractMeta.matched_vendor_id && extractMeta.vendor_name_seen && (
                            <div className="pt-1">
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={openCreateVendorFromPi}
                                data-testid="po-create-vendor-from-pi"
                                className="border-emerald-600 text-emerald-800 hover:bg-emerald-100"
                              >
                                <Plus className="h-3.5 w-3.5 mr-1.5" />
                                Create vendor "{extractMeta.vendor_name_seen}"
                              </Button>
                            </div>
                          )}
                          {pendingDocument?.filename && (
                            <div className="text-emerald-800/70">
                              Document <strong>{pendingDocument.filename}</strong> will be attached
                              automatically once you click <strong>Create PO</strong>.
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </TabsContent>
                </Tabs>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="flex items-center justify-between">
                        <Label htmlFor="po_number">PO Number *</Label>
                        {!editingPO && poNumberAutoSuggested && (
                          <span className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold" data-testid="po-number-autogen-badge">
                            Auto
                          </span>
                        )}
                      </div>
                      <Input
                        id="po_number"
                        value={formData.po_number}
                        onChange={(e) => {
                          setFormData({...formData, po_number: e.target.value});
                          setPoNumberAutoSuggested(false);
                        }}
                        placeholder={editingPO ? '' : 'Auto-generated from tournament + date'}
                        required
                        data-testid="po-number-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="vendor_id">Vendor *</Label>
                      <Select value={formData.vendor_id} onValueChange={(value) => setFormData({...formData, vendor_id: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select vendor" />
                        </SelectTrigger>
                        <SelectContent>
                          {vendors.map((vendor) => (
                            <SelectItem key={vendor.id} value={vendor.id}>
                              {vendor.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="date">Date *</Label>
                      <Input
                        id="date"
                        type="date"
                        value={formData.date}
                        onChange={(e) => {
                          // Date is independent of Tournament/Fiscal Year —
                          // changing the PO date should not auto-shift the
                          // tournament tagging.
                          setFormData({...formData, date: e.target.value});
                        }}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="fiscal_year">Tournament *</Label>
                      <TournamentSelector 
                        value={formData.fiscal_year}
                        onChange={(value) => setFormData({...formData, fiscal_year: value, department: '', budget_head: ''})}
                      />
                    </div>
                  </div>

                  {formData.fiscal_year && departments.length === 0 && (
                    <div
                      data-testid="no-budgets-warning"
                      className="flex items-start gap-3 p-3 rounded-md border border-amber-300 bg-amber-50 text-sm"
                    >
                      <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" strokeWidth={1.75} />
                      <div className="flex-1">
                        <p className="font-medium text-amber-900">
                          No budgets uploaded for {formData.fiscal_year} yet
                        </p>
                        <p className="text-amber-800 text-xs mt-0.5">
                          Upload a budget for this tournament before creating a PO.
                        </p>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setDialogOpen(false);
                          navigate('/budget-upload');
                        }}
                        className="border-amber-400 text-amber-900 hover:bg-amber-100"
                      >
                        Upload budget
                      </Button>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="department">Department *</Label>
                      <Select 
                        value={formData.department} 
                        onValueChange={(value) => setFormData({...formData, department: value, budget_head: ''})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select department" />
                        </SelectTrigger>
                        <SelectContent>
                          {departments.map((dept) => (
                            <SelectItem key={dept.department} value={dept.department}>
                              {dept.department}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="budget_head">Budget Head *</Label>
                      <Select 
                        value={formData.budget_head} 
                        onValueChange={(value) => setFormData({...formData, budget_head: value})}
                        disabled={!formData.department}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={formData.department ? "Select budget head" : "Select department first"} />
                        </SelectTrigger>
                        <SelectContent>
                          {budgetHeadsByDept.map((b) => (
                            <SelectItem key={b.head} value={b.head}>
                              {b.head}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Tax Fields */}
                  <div className="border-t pt-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Calculator className="h-4 w-4 text-muted-foreground" />
                      <h3 className="text-sm font-semibold">Tax Calculation</h3>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="taxable_value">Taxable Value (₹) *</Label>
                        <Input
                          id="taxable_value"
                          type="number"
                          step="0.01"
                          value={formData.taxable_value}
                          onChange={(e) => setFormData({...formData, taxable_value: e.target.value})}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="cgst">CGST (₹)</Label>
                        <Input
                          id="cgst"
                          type="number"
                          step="0.01"
                          value={formData.cgst}
                          onChange={(e) => setFormData({...formData, cgst: e.target.value})}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 mt-4">
                      <div>
                        <Label htmlFor="sgst">SGST (₹)</Label>
                        <Input
                          id="sgst"
                          type="number"
                          step="0.01"
                          value={formData.sgst}
                          onChange={(e) => setFormData({...formData, sgst: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label htmlFor="igst">IGST (₹)</Label>
                        <Input
                          id="igst"
                          type="number"
                          step="0.01"
                          value={formData.igst}
                          onChange={(e) => setFormData({...formData, igst: e.target.value})}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 mt-4">
                      <div>
                        <Label htmlFor="tds">TDS (₹)</Label>
                        <Input
                          id="tds"
                          type="number"
                          step="0.01"
                          value={formData.tds}
                          onChange={(e) => setFormData({...formData, tds: e.target.value})}
                        />
                      </div>
                      <div className="bg-muted/30 p-3 rounded-lg">
                        <Label className="text-xs text-muted-foreground">Net Payable</Label>
                        <div className="text-2xl font-bold text-primary mt-1">
                          ₹{calculateNetPayable().toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Per-leg allocation. Cricket P&L (Mens / Womens / Ground 2)
                      uses this to roll PO costs into the right leg. Defaults to
                      "Common" when a head isn't yet split. */}
                  <LegSplitInput
                    value={formData.leg_split}
                    onChange={(next) => {
                      setFormData({ ...formData, leg_split: next });
                      setLegSplitUserEdited(true);
                    }}
                    taxableValue={parseFloat(formData.taxable_value) || 0}
                    suggestion={legSuggestion}
                    onUseSuggestion={() => {
                      setFormData({ ...formData, leg_split: legSuggestion });
                      setLegSplitUserEdited(false);
                    }}
                    disableAutoDefault
                  />

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="notes">Notes</Label>
                      <Textarea
                        id="notes"
                        value={formData.notes}
                        onChange={(e) => setFormData({...formData, notes: e.target.value})}
                        rows={3}
                      />
                    </div>
                    <div>
                      <Label htmlFor="status">Status</Label>
                      <Select
                        value={formData.status}
                        onValueChange={(value) => setFormData({...formData, status: value})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="draft">Draft</SelectItem>
                          <SelectItem value="approved">Approved</SelectItem>
                          <SelectItem value="final">Final</SelectItem>
                          <SelectItem value="paid">Paid</SelectItem>
                          <SelectItem value="cancelled">Cancelled</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" disabled={submitting}>
                      {submitting ? 'Saving...' : editingPO ? 'Update PO' : 'Create PO'}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Create vendor from PI — nested mini-dialog */}
      <Dialog open={vendorCreateOpen} onOpenChange={setVendorCreateOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="create-vendor-from-pi-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-primary" />
              Create vendor from this PI
            </DialogTitle>
            <DialogDescription>
              {vendorPrefilling
                ? 'Reading the PI for full vendor details…'
                : 'Review the AI-extracted vendor info, fill any gaps, then save. The PI will be attached as a KYC document on this vendor.'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitCreateVendorFromPi} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>Vendor Name *</Label>
                <Input
                  required
                  value={vendorCreateForm.name}
                  onChange={(e) => setVendorCreateForm((p) => ({ ...p, name: e.target.value }))}
                  data-testid="cv-name"
                />
              </div>
              <div>
                <Label>GSTIN</Label>
                <Input
                  value={vendorCreateForm.gstin}
                  onChange={(e) => setVendorCreateForm((p) => ({ ...p, gstin: e.target.value }))}
                  data-testid="cv-gstin"
                />
              </div>
              <div>
                <Label>PAN</Label>
                <Input
                  value={vendorCreateForm.pan}
                  onChange={(e) => setVendorCreateForm((p) => ({ ...p, pan: e.target.value }))}
                  data-testid="cv-pan"
                />
              </div>
              <div>
                <Label>Bank Account Number</Label>
                <Input
                  value={vendorCreateForm.bank_account_number}
                  onChange={(e) => setVendorCreateForm((p) => ({ ...p, bank_account_number: e.target.value }))}
                  data-testid="cv-bank"
                />
              </div>
              <div>
                <Label>IFSC Code</Label>
                <Input
                  value={vendorCreateForm.ifsc_code}
                  onChange={(e) => setVendorCreateForm((p) => ({ ...p, ifsc_code: e.target.value }))}
                  data-testid="cv-ifsc"
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={vendorCreateForm.email}
                  onChange={(e) => setVendorCreateForm((p) => ({ ...p, email: e.target.value }))}
                  data-testid="cv-email"
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  value={vendorCreateForm.phone}
                  onChange={(e) => setVendorCreateForm((p) => ({ ...p, phone: e.target.value }))}
                  data-testid="cv-phone"
                />
              </div>
              <div className="col-span-2">
                <Label>Address</Label>
                <Textarea
                  rows={2}
                  value={vendorCreateForm.address}
                  onChange={(e) => setVendorCreateForm((p) => ({ ...p, address: e.target.value }))}
                  data-testid="cv-address"
                />
              </div>
              <div className="col-span-2">
                <Label>Remarks</Label>
                <Input
                  value={vendorCreateForm.remarks}
                  onChange={(e) => setVendorCreateForm((p) => ({ ...p, remarks: e.target.value }))}
                  data-testid="cv-remarks"
                />
              </div>
            </div>
            <DialogFooter className="pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setVendorCreateOpen(false)}
                disabled={vendorCreating}
                data-testid="cv-cancel"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={vendorCreating || vendorPrefilling || !vendorCreateForm.name?.trim()}
                data-testid="cv-submit"
              >
                {vendorCreating ? 'Creating…' : 'Create vendor & use'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Summary Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-border bg-gradient-to-br from-blue-500/10 to-blue-600/5">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total POs</p>
                <p className="text-3xl font-bold">{summaryMetrics.totalPOs}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-blue-500/20 flex items-center justify-center">
                <FileText className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-border bg-gradient-to-br from-emerald-500/10 to-emerald-600/5">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Taxable Value</p>
                <p className="text-2xl font-bold text-emerald-600">
                  ₹{summaryMetrics.totalTaxableValue.toLocaleString('en-IN')}
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-emerald-500/20 flex items-center justify-center">
                <IndianRupee className="h-6 w-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-border bg-gradient-to-br from-purple-500/10 to-purple-600/5">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Net Payable</p>
                <p className="text-2xl font-bold text-purple-600">
                  ₹{summaryMetrics.totalNetPayable.toLocaleString('en-IN')}
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-purple-500/20 flex items-center justify-center">
                <Calculator className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-border bg-gradient-to-br from-orange-500/10 to-orange-600/5">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Open Value</p>
                <p className="text-2xl font-bold text-orange-600">
                  ₹{summaryMetrics.totalOpenValue.toLocaleString('en-IN')}
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-orange-500/20 flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="border-border">
        <CardHeader className="border-b border-border/50">
          <CardTitle className="font-heading">All Purchase Orders</CardTitle>
          <CardDescription>
            {searchTerm ? `Showing ${filteredPOs.length} of ${pos.length} POs` : `Total: ${pos.length} POs`}
            {selectedFiscalYear !== 'all' && ` (FY ${selectedFiscalYear})`}
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-6">
          {filteredPOs.length === 0 ? (
            <div className="text-center py-12">
              <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" strokeWidth={1.5} />
              <p className="text-muted-foreground">{searchTerm ? 'No matching purchase orders found' : 'No purchase orders found'}</p>
              <p className="text-sm text-muted-foreground mt-1">
                Create your first purchase order to get started
              </p>
            </div>
          ) : (
            <div className="space-y-8">
              {Object.entries(groupedPOs).map(([department, deptPOs]) => (
                <div key={department}>
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <span className="bg-primary/10 text-primary px-3 py-1 rounded-full text-sm">
                      {department}
                    </span>
                    <span className="text-muted-foreground text-sm">({deptPOs.length} POs)</span>
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-muted/50 text-muted-foreground uppercase text-xs tracking-wider font-semibold">
                          <th className="text-left py-3 px-4">PO Number</th>
                          <th className="text-left py-3 px-4">Vendor</th>
                          <th className="text-left py-3 px-4">Budget Head</th>
                          <th className="text-left py-3 px-4">Leg</th>
                          <th className="text-left py-3 px-4">Date</th>
                          <th className="text-right py-3 px-4">Taxable Value</th>
                          <th className="text-right py-3 px-4">Open Value</th>
                          <th className="text-right py-3 px-4">CGST</th>
                          <th className="text-right py-3 px-4">SGST</th>
                          <th className="text-right py-3 px-4">IGST</th>
                          <th className="text-right py-3 px-4">TDS</th>
                          <th className="text-right py-3 px-4">Net Payable</th>
                          <th className="text-center py-3 px-4">Status</th>
                          {canManagePOs && <th className="text-center py-3 px-4">Actions</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {deptPOs.map((po) => (
                          <tr
                            key={po.id}
                            data-testid={`po-row-${po.po_number}`}
                            className="border-b border-border hover:bg-muted/30 transition-colors cursor-pointer"
                            onClick={() => navigate(`/purchase-orders/${po.id}`)}
                          >
                            <td className="py-3 px-4 font-mono text-sm text-primary hover:underline">{po.po_number}</td>
                            <td className="py-3 px-4">{po.vendor_name}</td>
                            <td className="py-3 px-4 text-sm">{po.budget_head}</td>
                            <td className="py-3 px-4 text-sm" data-testid={`po-leg-${po.po_number}`}>
                              <LegBadges legSplit={po.leg_split} />
                            </td>
                            <td className="py-3 px-4 text-sm text-muted-foreground">
                              {new Date(po.date).toLocaleDateString()}
                            </td>
                            <td className="py-3 px-4 text-right font-medium">
                              ₹{po.taxable_value?.toLocaleString('en-IN') || 0}
                            </td>
                            <td className="py-3 px-4 text-right font-bold text-success">
                              ₹{po.open_value?.toLocaleString('en-IN') || po.taxable_value?.toLocaleString('en-IN') || 0}
                            </td>
                            <td className="py-3 px-4 text-right text-sm">
                              ₹{po.cgst?.toLocaleString('en-IN') || 0}
                            </td>
                            <td className="py-3 px-4 text-right text-sm">
                              ₹{po.sgst?.toLocaleString('en-IN') || 0}
                            </td>
                            <td className="py-3 px-4 text-right text-sm">
                              ₹{po.igst?.toLocaleString('en-IN') || 0}
                            </td>
                            <td className="py-3 px-4 text-right text-sm text-destructive">
                              ₹{po.tds?.toLocaleString('en-IN') || 0}
                            </td>
                            <td className="py-3 px-4 text-right font-bold text-primary">
                              ₹{po.net_payable?.toLocaleString('en-IN') || 0}
                            </td>
                            <td className="py-3 px-4 text-center">
                              {(() => {
                                const s = (po.status || 'draft').toLowerCase();
                                const variantMap = {
                                  draft: 'secondary',
                                  approved: 'default',
                                  final: 'default',
                                  paid: 'success',
                                  cancelled: 'destructive',
                                };
                                const labelMap = {
                                  draft: 'Draft',
                                  approved: 'Approved',
                                  final: 'Final',
                                  paid: 'Paid',
                                  cancelled: 'Cancelled',
                                };
                                return (
                                  <Badge variant={variantMap[s] || 'secondary'}>
                                    {labelMap[s] || po.status}
                                  </Badge>
                                );
                              })()}
                            </td>
                            {canManagePOs && (
                              <td className="py-3 px-4 text-center" onClick={(e) => e.stopPropagation()}>
                                <div className="flex gap-2 justify-center">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={(e) => { e.stopPropagation(); handleEdit(po); }}
                                  >
                                    <Edit className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={(e) => { e.stopPropagation(); handleDelete(po.id); }}
                                  >
                                    <Trash2 className="h-4 w-4 text-destructive" />
                                  </Button>
                                </div>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PurchaseOrders;